/**
 * Os blocos do profissional: tabela selecionável, escala encadeada e cálculo.
 *
 * Tudo aqui é **conta e vocabulário**, sem React: é o que permite testar a
 * regra sem montar tela, e é a mesma regra que o preenchimento usa e que a
 * impressão do prontuário vai usar. Duas implementações da classificação da OMS
 * dariam duas verdades sobre o mesmo paciente.
 *
 * Os presets vêm de `_profissional/presets-lote1.json`, do pacote clínico —
 * não foram inventados aqui. Glogau, grau da acne e Baumann são classificações
 * publicadas: mexer nas faixas seria mexer em conduta clínica.
 */

import { urlDaArteClinica } from './arteClinica';
import type { FaixaDeClassificacao, RespostaDaFicha } from './tipos';

/* ------------------------------------------------------ tabela selecionável */

export interface LinhaSelecionavel {
    /** A chave gravada. Nunca muda. */
    valor: string;
    celulas: string[];
}

export interface TabelaSelecionavel {
    slug: string;
    rotulo: string;
    descricao: string;
    colunas: string[];
    linhas: LinhaSelecionavel[];
}

/**
 * As tabelas do lote 1. A clínica escolhe uma pronta ou monta a sua.
 *
 * A linha inteira é o alvo, e não um radio no canto: o profissional lê a linha
 * para decidir, e o alvo tem de ser o que ele está lendo.
 */
export const TABELAS_SELECIONAVEIS: TabelaSelecionavel[] = [
    {
        slug: 'glogau',
        rotulo: 'Escala de Glogau',
        descricao: 'Fotoenvelhecimento. A faixa etária é orientativa, não critério.',
        colunas: ['Tipo', 'Faixa etária', 'Achado'],
        linhas: [
            { valor: 'i', celulas: ['Tipo I', 'Entre 20 e 30 anos', 'Sem rugas'] },
            { valor: 'ii', celulas: ['Tipo II', 'Entre 30 e 40 anos', 'Rugas em movimento'] },
            { valor: 'iii', celulas: ['Tipo III', 'Entre 40 e 60 anos', 'Rugas em repouso'] },
            { valor: 'iv', celulas: ['Tipo IV', 'Acima de 60 anos', 'Somente rugas'] },
        ],
    },
    {
        slug: 'grau-acne',
        rotulo: 'Grau da acne',
        descricao: 'Selecione a linha correspondente ao quadro observado.',
        colunas: ['Grau', 'Natureza', 'Classificação'],
        linhas: [
            { valor: 'i', celulas: ['Grau I', 'Não-inflamatória', 'Comedogênica'] },
            { valor: 'ii', celulas: ['Grau II', 'Inflamatória', 'Pápulo-pustulosa'] },
            { valor: 'iii', celulas: ['Grau III', 'Inflamatória', 'Nódulo-cística'] },
            { valor: 'iv', celulas: ['Grau IV', 'Inflamatória', 'Conglobata'] },
            { valor: 'v', celulas: ['Grau V', 'Inflamatória', 'Fulminans'] },
        ],
    },
];

export function tabelaPorSlug(slug: string | undefined): TabelaSelecionavel | undefined {
    return TABELAS_SELECIONAVEIS.find((t) => t.slug === slug);
}

/* -------------------------------------------------------- escala encadeada */

export interface OpcaoDaDimensao {
    valor: string;
    /** A letra do padrão internacional — não a inicial do rótulo. */
    letra: string;
    rotulo: string;
}

export interface DimensaoDaEscala {
    rotulo: string;
    opcoes: OpcaoDaDimensao[];
}

export interface EscalaEncadeada {
    slug: string;
    rotulo: string;
    descricao: string;
    ajuda: string;
    dimensoes: DimensaoDaEscala[];
}

export const ESCALAS_ENCADEADAS: EscalaEncadeada[] = [
    {
        slug: 'baumann',
        rotulo: 'Tipo de pele de Baumann',
        descricao: 'Quatro dimensões independentes. A sigla usa as letras do padrão internacional.',
        ajuda: 'O/D oleosa ou seca · S/R sensível ou resistente · P/N pigmentada ou não-pigmentada · W/T enrugada ou firme.',
        dimensoes: [
            {
                rotulo: 'Hidratação',
                opcoes: [
                    { valor: 'o', letra: 'O', rotulo: 'Oleosa' },
                    { valor: 'd', letra: 'D', rotulo: 'Seca' },
                ],
            },
            {
                rotulo: 'Sensibilidade',
                opcoes: [
                    { valor: 's', letra: 'S', rotulo: 'Sensível' },
                    { valor: 'r', letra: 'R', rotulo: 'Resistente' },
                ],
            },
            {
                rotulo: 'Pigmentação',
                opcoes: [
                    { valor: 'p', letra: 'P', rotulo: 'Pigmentada' },
                    { valor: 'n', letra: 'N', rotulo: 'Não-pigmentada' },
                ],
            },
            {
                rotulo: 'Firmeza',
                opcoes: [
                    { valor: 'w', letra: 'W', rotulo: 'Enrugada' },
                    { valor: 't', letra: 'T', rotulo: 'Firme' },
                ],
            },
        ],
    },
];

export function escalaEncadeadaPorSlug(slug: string | undefined): EscalaEncadeada | undefined {
    return ESCALAS_ENCADEADAS.find((e) => e.slug === slug);
}

/**
 * A sigla montada a partir das dimensões respondidas.
 *
 * Vazia enquanto falta alguma: meia sigla não é um tipo de pele, é um palpite —
 * e "OS" lido como resultado faria alguém procurar um tipo que não existe.
 *
 * As letras são as do padrão internacional (O/D, S/R, P/N, W/T), e não a
 * inicial do rótulo em português: Seca e Sensível colidiriam no "S".
 */
export function siglaDaEscala(escala: EscalaEncadeada, respostas: string[]): string {
    if (respostas.length !== escala.dimensoes.length) return '';
    const letras = escala.dimensoes.map((d, i) => d.opcoes.find((o) => o.valor === respostas[i])?.letra);
    return letras.every((l): l is string => !!l) ? letras.join('') : '';
}

/* ------------------------------------------------------------------- IMC */

export type ClasseDeImc =
    | 'baixo-peso' | 'eutrofico' | 'sobrepeso'
    | 'obesidade-1' | 'obesidade-2' | 'obesidade-3';

export interface ResultadoDeImc {
    /** Já arredondado a uma casa — é o número que a tela mostra. */
    valor: number;
    classe: ClasseDeImc;
    rotulo: string;
}

const FAIXAS_DA_OMS: { ate: number; classe: ClasseDeImc; rotulo: string }[] = [
    { ate: 18.4, classe: 'baixo-peso', rotulo: 'Baixo peso' },
    { ate: 24.9, classe: 'eutrofico', rotulo: 'Peso adequado' },
    { ate: 29.9, classe: 'sobrepeso', rotulo: 'Sobrepeso' },
    { ate: 34.9, classe: 'obesidade-1', rotulo: 'Obesidade grau I' },
    { ate: 39.9, classe: 'obesidade-2', rotulo: 'Obesidade grau II' },
    { ate: Infinity, classe: 'obesidade-3', rotulo: 'Obesidade grau III' },
];

/**
 * IMC e classificação da OMS.
 *
 * **A classificação segue o número EXIBIDO**, já arredondado a uma casa. Sem
 * isso, 68 kg e 1,65 m dão 24,98: a tela mostraria "25,0" e o prontuário diria
 * "Peso adequado" — mas 25,0 é, por definição, sobrepeso, e o profissional leria
 * uma contradição na própria ficha. Foi um erro real encontrado em teste do
 * pacote clínico, e é por isso que o arredondamento vem ANTES da faixa.
 *
 * `altura` em metros. Nulo quando falta um dos dois, ou quando o valor não é
 * medida de gente — 0 kg e 12 m não são erro de digitação a corrigir sozinho.
 */
export function calcularImc(pesoKg: number | undefined, alturaM: number | undefined): ResultadoDeImc | null {
    if (!pesoKg || !alturaM) return null;
    if (pesoKg <= 0 || pesoKg > 500 || alturaM <= 0.4 || alturaM > 2.6) return null;

    const valor = Math.round((pesoKg / (alturaM * alturaM)) * 10) / 10;
    const faixa = FAIXAS_DA_OMS.find((f) => valor <= f.ate) ?? FAIXAS_DA_OMS[FAIXAS_DA_OMS.length - 1]!;
    return { valor, classe: faixa.classe, rotulo: faixa.rotulo };
}

/** A silhueta do acervo para o resultado. Vazia quando o sexo não foi informado. */
export function silhuetaDoImc(classe: ClasseDeImc, sexo: 'feminino' | 'masculino' | ''): string {
    return sexo ? urlDaArteClinica(`silhuetas/imc-${sexo}-${classe}.svg`) : '';
}

/* ------------------------------------------------------------- perimetria */

/** As circunferências do preset, na ordem em que se mede. */
export const MEDIDAS_DA_PERIMETRIA = [
    'Braço', 'Braço contraído', 'Tórax', 'Cintura', 'Quadril',
    'Coxa mediana', 'Panturrilha', 'Úmero', 'Fêmur',
];

export interface ResultadoDaPerimetria {
    /** Cintura ÷ quadril, com duas casas. */
    rcq: number;
    /**
     * O risco pela OMS, quando o sexo foi informado.
     *
     * Sem sexo não há classificação: os pontos de corte são diferentes, e usar
     * um deles para todo mundo classificaria metade das pessoas errado.
     */
    risco?: 'aumentado' | 'dentro-do-esperado';
}

export function calcularPerimetria(
    cintura: number | undefined,
    quadril: number | undefined,
    sexo: 'feminino' | 'masculino' | '',
): ResultadoDaPerimetria | null {
    if (!cintura || !quadril || cintura <= 0 || quadril <= 0) return null;

    const rcq = Math.round((cintura / quadril) * 100) / 100;
    if (!sexo) return { rcq };

    /* Pontos de corte da OMS: 0,90 para homens, 0,85 para mulheres. */
    const corte = sexo === 'masculino' ? 0.90 : 0.85;
    return { rcq, risco: rcq > corte ? 'aumentado' : 'dentro-do-esperado' };
}

/* ------------------------------------------------------------ adipometria */

/**
 * Adipometria: dobras cutâneas.
 *
 * **Três leituras por dobra, e vale a MEDIANA.** É o que o preset do pacote
 * pede ("três medidas por dobra, no lado direito"), e a mediana e não a média
 * porque uma pega errada — pinçar músculo junto — vira um valor muito acima dos
 * outros dois; a média carregaria esse erro para o resultado, a mediana o
 * descarta.
 *
 * **As equações são publicadas, e estão nomeadas uma a uma.** Jackson & Pollock
 * é o protocolo que a clínica de estética usa no Brasil; Siri converte
 * densidade em percentual. Nenhum coeficiente daqui foi escolhido por este
 * projeto — e é por isso que trocar um deles é decisão clínica, não ajuste.
 *
 * **O que este arquivo NÃO faz é classificar.** A faixa de "bom / médio / alto"
 * para percentual de gordura muda conforme a tabela adotada (ACSM, Pollock &
 * Wilmore, Lohman), e não há uma que seja a oficial do jeito que a da OMS é
 * para o IMC. Cuspir uma faixa sem dizer de onde ela veio seria inventar
 * conduta. O número sai; a leitura dele é de quem atende.
 */

export type SlugDeProtocolo = 'somatorio' | 'faulkner' | 'pollock-3';

export interface ProtocoloDeAdipometria {
    slug: SlugDeProtocolo;
    rotulo: string;
    /** A publicação de onde vem a equação. Aparece na tela, junto do resultado. */
    fonte: string;
    descricao: string;
    /** As dobras, na ordem em que se mede. Por sexo quando o protocolo muda com ele. */
    dobras: { feminino: string[]; masculino: string[] };
    /** Sem sexo e idade a equação não fecha. Faulkner e somatório não precisam. */
    exigeSexoEIdade: boolean;
}

/**
 * Os nomes das dobras são os do pacote clínico, letra por letra.
 *
 * "Tricipital" e não "Tríceps"; "Ilíaca" no somatório e "Suprailíaca" no
 * Faulkner, como está lá. Não é descuido copiado: o nome é a CHAVE da resposta
 * no prontuário, e renomear depois deixaria a ficha antiga com uma medida que
 * a tela não sabe mais onde encaixar.
 */
const NOVE_DOBRAS = [
    'Tricipital', 'Subescapular', 'Bicipital', 'Axilar', 'Ilíaca',
    'Supraespinhal', 'Abdominal', 'Coxa', 'Panturrilha',
];

const QUATRO_DE_FAULKNER = ['Tricipital', 'Subescapular', 'Suprailíaca', 'Abdominal'];

export const PROTOCOLOS_DE_ADIPOMETRIA: ProtocoloDeAdipometria[] = [
    {
        slug: 'somatorio',
        rotulo: 'Somatório de dobras (sem conversão)',
        fonte: 'Sem equação — é a soma das medidas.',
        descricao: 'As nove dobras do pacote, somadas. Acompanha evolução sem afirmar percentual de gordura.',
        dobras: { feminino: NOVE_DOBRAS, masculino: NOVE_DOBRAS },
        exigeSexoEIdade: false,
    },
    {
        slug: 'faulkner',
        rotulo: 'Faulkner (4 dobras)',
        fonte: 'Faulkner (1968): %G = Σ4 × 0,153 + 5,783.',
        descricao: 'Quatro dobras, equação linear. Não depende de sexo nem de idade.',
        dobras: { feminino: QUATRO_DE_FAULKNER, masculino: QUATRO_DE_FAULKNER },
        exigeSexoEIdade: false,
    },
    {
        slug: 'pollock-3',
        rotulo: 'Pollock 3 dobras',
        fonte: 'Jackson & Pollock (1978) para homens; Jackson, Pollock & Ward (1980) para mulheres. Percentual por Siri (1961).',
        descricao: 'As dobras MUDAM com o sexo: a equação foi derivada em cada população com os pontos dela.',
        dobras: {
            masculino: ['Peitoral', 'Abdominal', 'Coxa'],
            feminino: ['Tricipital', 'Suprailíaca', 'Coxa'],
        },
        exigeSexoEIdade: true,
    },
];

export function protocoloPorSlug(slug: string | undefined): ProtocoloDeAdipometria | undefined {
    return PROTOCOLOS_DE_ADIPOMETRIA.find((p) => p.slug === slug);
}

/** As dobras de um protocolo para um sexo. Sem sexo, mostra as do masculino só para dimensionar a tela. */
export function dobrasDoProtocolo(protocolo: ProtocoloDeAdipometria, sexo: 'feminino' | 'masculino' | ''): string[] {
    return protocolo.dobras[sexo === 'feminino' ? 'feminino' : 'masculino'];
}

/**
 * A mediana das três leituras de uma dobra.
 *
 * Com uma ou duas leituras responde assim mesmo — o profissional que mediu
 * duas vezes tem um número, e esconder o resultado até a terceira faria a tela
 * parecer travada. Com duas, é a média; a mediana de um par não existe.
 */
export function medianaDaDobra(leituras: (number | undefined)[]): number | undefined {
    const validas = leituras.filter((l): l is number => typeof l === 'number' && l > 0 && l <= 80).sort((a, b) => a - b);
    if (validas.length === 0) return undefined;
    if (validas.length === 2) return Math.round(((validas[0]! + validas[1]!) / 2) * 10) / 10;
    return validas[Math.floor(validas.length / 2)]!;
}

export interface ResultadoDaAdipometria {
    /** A soma das medianas, em milímetros. Existe em todo protocolo. */
    soma: number;
    /** Quantas dobras o protocolo pede e quantas têm leitura. */
    medidas: number;
    exigidas: number;
    /** Densidade corporal, quando a equação do protocolo fecha. */
    densidade?: number;
    /** Percentual de gordura por Siri, uma casa. */
    percentual?: number;
    /** Só com o peso: massa gorda e massa magra em quilos, uma casa. */
    massaGorda?: number;
    massaMagra?: number;
}

/**
 * Os coeficientes publicados, num lugar só.
 *
 * `Db = a − b·Σ + c·Σ² − idade·anos`. Conferidos, número a número, contra a
 * implementação do pacote clínico (`_base/vecchia-blocos-profissional.js`).
 * Ficam à vista e juntos porque trocar qualquer um deles muda o resultado
 * clínico de toda ficha já preenchida com aquele protocolo — e porque o limite
 * de validade da equação é calculado a partir deles, e não pode divergir deles.
 */
const EQUACOES: Record<'feminino' | 'masculino', { a: number; b: number; c: number; idade: number }> = {
    masculino: { a: 1.10938, b: 0.0008267, c: 0.0000016, idade: 0.0002574 },
    feminino: { a: 1.0994921, b: 0.0009929, c: 0.0000023, idade: 0.0001392 },
};

/** Siri (1961): percentual de gordura a partir da densidade corporal. */
function siri(densidade: number): number {
    return 495 / densidade - 450;
}

/**
 * O resultado da adipometria.
 *
 * Só devolve percentual quando o protocolo pede equação E tem tudo de que ela
 * precisa: **todas** as dobras, sexo e idade. Faltando uma dobra, a soma
 * continua saindo (é acompanhamento legítimo), mas o percentual não — meia
 * equação de Pollock não é um percentual menor, é um número errado.
 */
export function calcularAdipometria(
    protocolo: ProtocoloDeAdipometria,
    medianas: Record<string, number | undefined>,
    sexo: 'feminino' | 'masculino' | '',
    idade: number | undefined,
    pesoKg?: number | undefined,
): ResultadoDaAdipometria | null {
    const dobras = dobrasDoProtocolo(protocolo, sexo);
    const lidas = dobras.map((d) => medianas[d]).filter((v): v is number => typeof v === 'number' && v > 0);
    if (lidas.length === 0) return null;

    const soma = Math.round(lidas.reduce((t, v) => t + v, 0) * 10) / 10;
    const base: ResultadoDaAdipometria = { soma, medidas: lidas.length, exigidas: dobras.length };

    const completo = lidas.length === dobras.length;
    if (!completo) return base;

    /* Faulkner é linear e não depende de sexo nem de idade — não tem densidade
       para devolver, só o percentual. */
    if (protocolo.slug === 'faulkner') {
        const bruto = soma * 0.153 + 5.783;
        return bruto > 0 && bruto < 70
            ? comPeso({ ...base, percentual: Math.round(bruto * 10) / 10 }, pesoKg)
            : base;
    }

    /* Só o Pollock tem equação de densidade. Sem este corte, o somatório com
       sexo e idade preenchidos cairia na equação de TRÊS dobras carregando a
       soma de NOVE — e devolveria um percentual plausível e errado. */
    if (protocolo.slug !== 'pollock-3') return base;
    if (!sexo || !idade || idade < 5 || idade > 120) return base;

    const eq = EQUACOES[sexo];

    /* **A soma não pode passar do vértice da parábola.** A equação tem um termo
       em S², e ele existe para achatar a curva no alto — mas passado o vértice
       ela VIRA, e mais gordura passa a dar percentual menor. Não é defeito de
       conta: é a equação usada fora do intervalo em que foi derivada. Na
       equação feminina isso começa em 216 mm, que três dobras de 72 mm
       alcançam — não é hipótese. O limite sai dos próprios coeficientes
       (`b / 2c`), e por isso não pode divergir deles. */
    if (soma >= eq.b / (2 * eq.c)) return base;

    const densidadeBruta = eq.a - eq.b * soma + eq.c * soma * soma - eq.idade * idade;

    const percentualBruto = siri(densidadeBruta);
    /* Rede de segurança, a mesma do pacote: densidade fora do humano não vira
       percentual. Melhor não responder do que responder -3%. */
    if (!Number.isFinite(percentualBruto) || percentualBruto <= 0 || percentualBruto >= 70) return base;

    const comEquacao: ResultadoDaAdipometria = {
        ...base,
        densidade: Math.round(densidadeBruta * 10000) / 10000,
        percentual: Math.round(percentualBruto * 10) / 10,
    };

    return comPeso(comEquacao, pesoKg);
}

/**
 * O rótulo da clínica para um percentual.
 *
 * Sem faixas não há classificação, e isso é o padrão — não existe tabela
 * oficial de percentual de gordura como a da OMS para o IMC, e escolher uma
 * pelo produto seria escolher conduta pela clínica.
 *
 * A faixa é achada pelo primeiro limite que alcança o valor; acima de todas,
 * vale a última. Por isso o editor pede que a última cubra o topo.
 */
export function classificarPercentual(
    percentual: number | undefined,
    faixas: FaixaDeClassificacao[] | undefined,
): string {
    if (percentual === undefined || !faixas?.length) return '';
    const ordenadas = [...faixas].filter((f) => f.rotulo.trim()).sort((a, b) => a.ate - b.ate);
    if (!ordenadas.length) return '';
    return (ordenadas.find((f) => percentual <= f.ate) ?? ordenadas[ordenadas.length - 1]!).rotulo.trim();
}

/** Massa gorda e massa magra, quando o peso foi informado. */
function comPeso(r: ResultadoDaAdipometria, pesoKg: number | undefined): ResultadoDaAdipometria {
    if (!pesoKg || pesoKg <= 0 || pesoKg > 500 || r.percentual === undefined) return r;
    const massaGorda = Math.round((pesoKg * r.percentual / 100) * 10) / 10;
    return { ...r, massaGorda, massaMagra: Math.round((pesoKg - massaGorda) * 10) / 10 };
}

/* --------------------------------------------------- leitura das respostas */

/**
 * O que a resposta de um bloco composto guarda.
 *
 * Tabela, cálculo e escala encadeada não cabem num valor único, então gravam um
 * objeto. Ele vai no mesmo `jsonb` das outras respostas — e como a ficha guarda
 * a definição do dia (`0011`), a leitura de um prontuário antigo continua
 * sabendo o que cada chave queria dizer.
 */
export interface RespostaComposta {
    [chave: string]: RespostaDaFicha | RespostaComposta | string[][];
}

/** Texto curto do que foi respondido, para a linha do tempo e a impressão. */
export function resumoDoBloco(tipo: string, resposta: unknown): string {
    if (resposta === undefined || resposta === null || resposta === '') return '';

    if (tipo === 'calculo-imc') {
        const r = resposta as { imc?: number; rotulo?: string };
        return r.imc ? `IMC ${r.imc.toFixed(1).replace('.', ',')} · ${r.rotulo ?? ''}`.trim() : '';
    }
    if (tipo === 'perimetria') {
        const r = resposta as { rcq?: number };
        return r.rcq ? `RCQ ${r.rcq.toFixed(2).replace('.', ',')}` : '';
    }
    if (tipo === 'escala-encadeada') {
        const r = resposta as { sigla?: string };
        return r.sigla ?? '';
    }
    if (tipo === 'tabela') {
        const linhas = resposta as string[][];
        const preenchidas = linhas.filter((l) => l.some((c) => c.trim())).length;
        return preenchidas ? `${preenchidas} ${preenchidas === 1 ? 'linha preenchida' : 'linhas preenchidas'}` : '';
    }
    if (tipo === 'adipometria') {
        const r = resposta as { percentual?: number; soma?: number };
        if (r.percentual) return `${r.percentual.toFixed(1).replace('.', ',')}% de gordura`;
        return r.soma ? `Somatório ${r.soma.toFixed(1).replace('.', ',')} mm` : '';
    }
    if (tipo === 'arquivo') {
        const arquivos = resposta as { nome: string }[];
        return arquivos.length ? `${arquivos.length} ${arquivos.length === 1 ? 'arquivo' : 'arquivos'}` : '';
    }
    if (Array.isArray(resposta)) return resposta.join(', ');
    return String(resposta);
}
